/**
 * Copyright (c) [2026] SUSE Linux
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { X, AlertCircle, CheckCircle, Info, AlertTriangle } from 'react-feather';

const ICON_MAP = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  error: AlertCircle,
};

function Toast({ id, type = 'info', message, duration = 1500, onClose, showClose = true }) {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);
  const Icon = ICON_MAP[type] || ICON_MAP.info;

  useEffect(() => {
    if (!isVisible) {
      const timer = setTimeout(() => onClose(id), 300); // Wait for exit animation
      return () => clearTimeout(timer);
    }
  }, [isVisible, id, onClose]);

  useEffect(() => {
    if (duration > 0) {
      const startTime = Date.now();
      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, duration - elapsed);
        const progressPercent = (remaining / duration) * 100;
        setProgress(progressPercent);

        if (remaining <= 0) {
          setIsVisible(false);
          clearInterval(interval);
        }
      }, 50);

      return () => clearInterval(interval);
    }
  }, [duration]);

  const handleClose = () => {
    setIsVisible(false);
  };

  const typeClasses = {
    info: 'bg-blue-50 border-blue-200 text-blue-800',
    success: 'bg-green-50 border-green-200 text-green-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    error: 'bg-red-50 border-red-200 text-red-800',
  };

  const iconClasses = {
    info: 'text-blue-500',
    success: 'text-green-500',
    warning: 'text-yellow-500',
    error: 'text-red-500',
  };

  return (
    <div
      className={`
        relative w-80 max-w-sm mb-3 p-4 rounded-lg border shadow-lg
        transform transition-all duration-300 ease-in-out
        ${typeClasses[type]}
        ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'}
      `}
      role="alert"
      aria-live="polite"
      aria-atomic="true"
    >
      {/* Progress bar */}
      {duration > 0 && (
        <div className="absolute top-0 left-0 right-0 h-1 bg-gray-200 rounded-t-lg overflow-hidden">
          <div
            className={`h-full transition-all duration-100 ease-linear ${
              type === 'info' ? 'bg-blue-500' :
                type === 'success' ? 'bg-green-500' :
                  type === 'warning' ? 'bg-yellow-500' :
                    'bg-red-500'
            }`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      <div className="flex items-start">
        <div className={`flex-shrink-0 mr-3 ${iconClasses[type]}`}>
          <Icon size={20} />
        </div>
        <div className="flex-1">
          <p className="text-sm font-medium">{message}</p>
        </div>
        {showClose && (
          <button
            type="button"
            className="flex-shrink-0 ml-3 text-gray-400 hover:text-gray-600 focus:outline-none"
            onClick={handleClose}
            aria-label="Close notification"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

Toast.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  type: PropTypes.oneOf(['info', 'success', 'warning', 'error']),
  message: PropTypes.string.isRequired,
  duration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  showClose: PropTypes.bool,
};

export default Toast;